import { ChangeEvent, useEffect, useState } from "react";
import Badge from "@/components/Badge";
import Button from "@/components/Button";
import Input from "@/components/Input";
import Pagination from "@/components/Pagination";
import Table from "@/components/Table";
import HeaderColumn from "@/components/Table/table-header/HeaderColumn";
import Toogle from "@/components/Toogle";
import Modal from "@/components/Modal";
import Dropdown from "@/components/Dropdown";
import TextArea from "@/components/TextArea";
import HeaderColumnSort from "@/components/Table/table-header/HeaderColumnSort";
import { ReactComponent as EDIT_ICON } from "@/assets/icons/edit-black.svg";
import { ReactComponent as TRASH_ICON } from "@/assets/icons/trash.svg";
import { Warehouse, WarehouseID } from "@/@core/entity/warehouse.entity";
import getWarehouseInteractor from "@/@core/interactor/warehouse/get-warehouse.interactor";
import { stringToDate } from "@/utils/date";
import getUserInteractor from "@/@core/interactor/user/get-users.interactor";
import { User } from "@/@core/entity/user.entity";
import createWarehouseInteractor from "@/@core/interactor/warehouse/create-warehouse.interactor";
import { Toaster, toast } from "react-hot-toast";
import deleteWarehouseInteractor from "@/@core/interactor/warehouse/delete-warehouse.interactor";
import editWarehouseInteractor from "@/@core/interactor/warehouse/edit-warehouse.interactor";

interface TableSectionProps {
  showModalAdd: boolean;
  setShowModalAdd: React.Dispatch<React.SetStateAction<boolean>>;
}

let debounceSearch: any;

const TableSection = (props: TableSectionProps) => {
  const { showModalAdd, setShowModalAdd } = props;

  const [warehouseList, setWarehouseList] = useState<Warehouse[]>([]);
  const [userList, setUserList] = useState<User[]>([]);
  const [checkToggle, setCheckToggle] = useState<boolean>(false);
  const [isShowModalDelete, setIsShowModalDelete] = useState<boolean>(false);

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseID>();

  const [warehouseName, setWarehouseName] = useState<string>("");
  const [warehouseAddress, setWarehouseAddress] = useState<string>("");
  const [warehousePhone, setWarehousePhone] = useState<string>("");
  const [PIC, setPIC] = useState<any>();

  const [keyword, setKeyword] = useState<string>("");
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  // const [sort, setSort] = useState<string>("");

  const getWarehouseList = async (page: number, keyword = "") => {
    try {
      const response = await getWarehouseInteractor({
        page,
        keyword,
        limit: 5,
        // sort,
      });

      const {
        data: { data, pagination },
      } = response;

      setWarehouseList(data);
      if (pagination) {
        setActivePage(pagination.current_page);
        setTotalPage(pagination.last_page);
      }
    } catch (err) {
      setWarehouseList([]);
      setActivePage(1);
      setTotalPage(1);
    } finally {
      resetForm();
    }
  };

  const getUserList = async () => {
    try {
      const response = await getUserInteractor({
        page: 1,
        keyword,
        role: "",
        limit: 100,
      });

      const {
        data: { data },
      } = response;

      setUserList(data);
    } catch (err) {
      setUserList([]);
    }
  };

  const getBody = () => {
    return {
      name: warehouseName,
      address: warehouseAddress,
      phone: warehousePhone,
      pic_name: PIC.label,
      is_active: true,
    };
  };

  const handleCreateEditWarehouse = async () => {
    try {
      const body = getBody();
      if (isEdit) {
        const response = await editWarehouseInteractor({
          ...body,
          id: selectedWarehouse,
        });
        const { status } = response;

        if (status === 200) {
          toast.success("Updated");
          getWarehouseList(activePage);
          setShowModalAdd(!showModalAdd);
        }
      } else {
        const response = await createWarehouseInteractor(body);
        const { status } = response;

        if (status === 201) {
          toast.success("Created");
          getWarehouseList(activePage);
          setShowModalAdd(!showModalAdd);
        }
      }
    } catch (error: any) {
      const { response } = error;
      const {
        data: { message },
      } = response;

      toast.error(message);
    }
  };

  const updateStatusWarehouse = async (warehouse: Warehouse) => {
    try {
      const body = {
        id: warehouse.id,
        is_active: !warehouse.is_active,
        name: warehouse.name,
        address: warehouse.address,
        phone: warehouse.phone,
        pic_name: warehouse.pic_name,
      };

      const response = await editWarehouseInteractor(body);
      const { status } = response;

      if (status === 200) {
        toast.success("Updated");
        getWarehouseList(activePage);
      }
    } catch (error: any) {
      const { response } = error;
      const {
        data: { message },
      } = response;

      toast.error(message);
    }
  };

  const deleteWarehouse = async (id: WarehouseID) => {
    try {
      const response = await deleteWarehouseInteractor({ WarehouseId: id });
      const { status } = response;

      if (status === 200) {
        toast.success("Deleted");
        getWarehouseList(activePage);
        setIsShowModalDelete(!isShowModalDelete);
      }
    } catch (error: any) {
      const { response } = error;
      const {
        data: { message },
      } = response;

      toast.error(message);
    }
  };

  const resetForm = () => {
    setWarehouseName("");
    setWarehouseAddress("");
    setWarehousePhone("");
    setPIC("");
    setSelectedWarehouse(undefined);
  };

  const onChangePagination = (e: any) => {
    setActivePage(e);
    getWarehouseList(e);
  };

  const onChangeKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setKeyword(val);

    clearTimeout(debounceSearch);
    debounceSearch = setTimeout(() => {
      getWarehouseList(1, val);
    }, 2000);
  };

  const onKeywordEnter = (e: any) => {
    const keyCode = e.keyCode;
    const val = e.target.value;

    if (keyCode === 13) {
      clearTimeout(debounceSearch);
      getWarehouseList(1, val);
    }
  };

  const checkDisabled = () => {
    return !warehouseName || !warehouseAddress || !warehousePhone || !PIC;
  };

  useEffect(() => {
    getWarehouseList(activePage);
  }, []);

  useEffect(() => {
    getUserList();
  }, [showModalAdd]);

  return (
    <>
      <div className='table-control-section'>
        <div>
          <label>Search for Warehouse</label>
          <Input
            style={{ width: "360px" }}
            value={keyword}
            withIcon
            type='search'
            placeholder='Search'
            onChange={onChangeKeyword}
            onKeyDown={onKeywordEnter}
          />
        </div>
      </div>

      <div className='table-section'>
        <Table>
          <thead>
            <tr>
              <HeaderColumn label='Warehouse' alignLabel='text-left' />
              <HeaderColumn label='Address' alignLabel='text-left' />
              <HeaderColumn label='Phone' alignLabel='text-left' />
              <HeaderColumn label='PIC' alignLabel='text-left' />
              <HeaderColumn label='Status' alignLabel='text-left' />
              <HeaderColumnSort
                label='Created at'
                sortType={"asc"}
                onChange={(e) => {}}
                alignLabel='text-center'
              />
              <HeaderColumn label='' alignLabel='text-left' />
            </tr>
          </thead>
          <tbody>
            {warehouseList.map((warehouse) => (
              <tr key={warehouse.id}>
                <td width='25%'>
                  <div className='text-with-toogle-column'>
                    <Toogle
                      checked={warehouse?.is_active}
                      onChange={() => {
                        setSelectedWarehouse(warehouse.id);
                        updateStatusWarehouse(warehouse);
                        setCheckToggle(!checkToggle);
                      }}
                    />
                    <span>{warehouse?.name}</span>
                  </div>
                </td>
                <td width='20%'>
                  <div className='text'>
                    <span>{warehouse?.address}</span>
                  </div>
                </td>
                <td width='15%'>
                  <div className='text'>
                    <span>{warehouse?.phone}</span>
                  </div>
                </td>
                <td width='10%'>
                  <div className='text'>
                    <span>{warehouse?.pic_name}</span>
                  </div>
                </td>
                <td width='100px'>
                  <div className='item-center'>
                    <Badge
                      dot
                      dotColor={warehouse?.is_active ? "#17B26A" : "#667085"}
                      label={warehouse?.is_active ? "Active" : "Inactive"}
                      labelColor={warehouse?.is_active ? "#067647" : "#344054"}
                      backgroundColor={
                        warehouse?.is_active ? "#ECFDF3" : "#F2F4F7"
                      }
                    />
                  </div>
                </td>
                <td width='20%'>
                  <div className='text item-center'>
                    <span>
                      {stringToDate(warehouse?.created_at, "dd month yyyy")}
                    </span>
                  </div>
                </td>
                <td width='20%'>
                  <div className='cta-wrapper'>
                    <button
                      onClick={() => {
                        setSelectedWarehouse(warehouse.id);
                        setIsShowModalDelete(true);
                      }}>
                      <TRASH_ICON />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedWarehouse(warehouse.id);
                        setWarehouseName(warehouse.name);
                        setWarehouseAddress(warehouse.address);
                        setWarehousePhone(warehouse.phone);
                        setPIC({
                          label: warehouse.pic_name,
                          value: warehouse.pic_id,
                        });
                        setIsEdit(true);
                        setShowModalAdd(true);
                      }}>
                      <EDIT_ICON />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination
          activePage={activePage}
          totalPage={totalPage}
          onChange={onChangePagination}
        />
      </div>

      {showModalAdd && (
        <Modal
          setIsOpen={() => {
            if (isEdit) {
              setIsEdit(false);
            }
            setShowModalAdd(!showModalAdd);
            resetForm();
          }}
          type='warehouse'
          title={`${isEdit ? "Edit" : "Add"} Warehouse`}
          subtitle={`${isEdit ? "Edit" : "Add"} Warehouse Description`}
          withDevider
          content={
            <div className='modal-content'>
              <div className='row'>
                <div className='label'>
                  <span>Warehouse Name</span>
                </div>
                <div className='input'>
                  <Input
                    style={{ width: "400px" }}
                    type='text'
                    withIcon={false}
                    placeholder='e.g. warehouse sunter'
                    value={warehouseName}
                    onChange={(e) => setWarehouseName(e.target.value)}
                  />
                </div>
              </div>
              <div className='divider' />
              <div className='row'>
                <div className='label'>
                  <span>Warehouse Address</span>
                </div>
                <div className='input'>
                  <TextArea
                    value={warehouseAddress}
                    style={{ width: "400px", height: "130px" }}
                    maxLength={2000}
                    withCounter={false}
                    onChange={(e) => setWarehouseAddress(e.target.value)}
                    placeholder='e.g. Komplek Podomoro Sport Centre, Jl. Danau Sunter Selatan Blok M - 1'
                  />
                </div>
              </div>
              <div className='divider' />
              <div className='row'>
                <div className='label'>
                  <span>Warehouse Phone</span>
                </div>
                <div className='input'>
                  <Input
                    style={{ width: "400px" }}
                    type='number'
                    withIcon={false}
                    placeholder='+62 8321 1231 2112'
                    value={warehousePhone}
                    onChange={(e) => setWarehousePhone(e.target.value)}
                  />
                </div>
              </div>
              <div className='divider' />
              <div className='row'>
                <div className='label'>
                  <span>Warehouse PIC</span>
                </div>
                <div className='input'>
                  <Dropdown
                    placeHolder='Select User'
                    value={PIC}
                    options={userList?.map((user) => {
                      const option = {
                        label: user?.fullname,
                        value: user?.id,
                      };
                      return option;
                    })}
                    onChange={(e) => setPIC(e)}
                    disabled={false}
                    optionType='text-option'
                    width='400px'
                  />
                </div>
              </div>
            </div>
          }
          footer={
            <div className='modal-footer'>
              <Button
                style={{ width: "270px" }}
                type='secondary'
                label='Cancel'
                onClick={() => {
                  if (isEdit) {
                    setIsEdit(false);
                  }
                  setShowModalAdd(!showModalAdd);
                  resetForm();
                }}
              />
              <Button
                style={{ width: "270px" }}
                type='primary'
                label='Save'
                onClick={handleCreateEditWarehouse}
                disabled={checkDisabled()}
              />
            </div>
          }
        />
      )}

      {isShowModalDelete && (
        <div className='delete-warehouse-modal-wrapper'>
          <Modal
            header={"header"}
            content={
              <div className='content-delete-warehouse'>
                <h2 className='modal-title'>Delete Warehouse</h2>
                <span className='desc'>
                  Are you sure you want to delete this warehouse? This action
                  cannot be undone.
                </span>
              </div>
            }
            type='delete'
            setIsOpen={() => setIsShowModalDelete(!isShowModalDelete)}
            footer={
              <div className='delete-warehouse-modal-btn-wrapper'>
                <Button
                  onClick={() => setIsShowModalDelete(!isShowModalDelete)}
                  label='Cancel'
                  type='secondary'
                  size='md'
                />
                <Button
                  onClick={() => deleteWarehouse(selectedWarehouse)}
                  label='Delete'
                  type='warning'
                  size='md'
                />
              </div>
            }
          />
        </div>
      )}
      <Toaster />
    </>
  );
};

export default TableSection;
